// src/components/NewsSection.js
import React from 'react';
import styled from 'styled-components';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { AnimatedSection } from './common/AnimatedSection';

const NewsSectionWrapper = styled.section`
  background: linear-gradient(#111111 10%, white 80%);
  padding-top: 6rem;
  padding-bottom: 3rem;
  @media (max-width: 768px) {
    background: linear-gradient(black 1%, white 99%);
    text-align: left;
    font-size: 1.8rem;
    padding-bottom: 1rem;
  }
`;

const Container = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 3rem; 

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.3rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 1);
  text-align: center;      /* 기본: 가운데 정렬(그대로) */
  margin-bottom: 4rem;
  letter-spacing: -1px;

  /* ✅ 모바일에서만 좌측 정렬 (유일한 변경) */
  @media (max-width: 768px) {
    text-align: left;
    font-size: 1.8rem;
    margin-bottom: 2rem;
    padding-left: 1rem;
  }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 340px), 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const NewsCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 0;
  box-shadow: 0 8px 32px rgba(59, 130, 246, 0.08);
  transition: all 0.3s ease;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(59, 130, 246, 0.12);
  }
`;

const NewsImageContainer = styled.div`
  width: 100%;
  height: 350px; /* 그대로 */
  overflow: hidden;
  position: relative;
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const NewsImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${NewsCard}:hover & {
    transform: scale(1.05);
  }
`;

const NewsImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 1rem;
  font-weight: 500;
`;

const NewsContent = styled.div`
  padding: 2rem;
  @media (max-width: 768px) {
    padding: 0.8rem;
  }
`;

const NewsTitle = styled.h3`
  font-size: 1.2rem;
  color: #111111;
  margin-bottom: 0.8rem;
  font-weight: 600;
  line-height: 1.4;
`;

const NewsDate = styled.div`
  color: #3b82f6;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  font-weight: 600;
  @media (max-width: 768px) {
    margin-bottom: 0.5rem;
  }
`;

const MoreButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end; /* 오른쪽 정렬 유지 */
  margin-top: 3rem;
  padding-right: 1rem;

  @media (max-width: 768px) {
    margin-top: 1rem;
  }
`;

const MoreButton = styled.button`
  background: none;
  border: none;
  color: #000000ce;
  padding: 0.5rem 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 0;
    height: 2px;
    background: #000000ff;
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }

  &:hover {
    color: #000000ff;
    transform: translateX(3px);
  }

  &:hover::after {
    width: 100%;
  }

  &:active {
    transform: translateX(0);
  }
`;

// 데이터 그대로
const newsData = [
  {
    title: "[포토닉스 코리아 2023]세연이앤에스, 캐리어형 비상표시장치·스마트 안전경고시스템 등 출품",
    date: "2023-11-15",
    image: "/images/news/news_231115.jpg",
    link: "https://www.mdilbo.com/detail/SexeuZ/703495"
  },
  {
    title: "㈜세연이앤에스 등 13개 기업, '예비-명품강소기업' 선정",
    date: "2023-09-24",
    image: "/images/news/news1.jpg",
    link: "https://www.mdilbo.com/detail/SexeuZ/703495"
  },
  {
    title: "광주광역시, ‘전국에서 가장 안전한 광주 만들기’ 제2호 안전마을, 안전학교, 안전기업 인증·협약식 개최",
    date: "2021-03-29",
    image: "/images/news/news3.jpg",
    link: "https://www.igj.co.kr/news/articleView.html?idxno=197943"
  }
];

function NewsSection() {
  const [titleRef, titleVisible] = useScrollAnimation();
  const [gridRef, gridVisible] = useScrollAnimation();
  const [buttonRef, buttonVisible] = useScrollAnimation();

  const handleNewsClick = (link) => {
    if (link) window.open(link, '_blank');
  };

  const handleMoreClick = () => {
    window.location.href = '/community/news';
  };

  return (
    <NewsSectionWrapper>
      <Container>
        <AnimatedSection ref={titleRef} isVisible={titleVisible} animation="fadeInUp">
          <SectionTitle>NEWS</SectionTitle>
        </AnimatedSection>

        <AnimatedSection ref={gridRef} isVisible={gridVisible} animation="fadeInUp" delay="0.2s">
          <NewsGrid>
            {newsData.map((news, index) => (
              <NewsCard key={index} onClick={() => handleNewsClick(news.link)}>
                <NewsImageContainer>
                  {news.image ? (
                    <NewsImage
                      src={news.image}
                      alt={news.title}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <NewsImagePlaceholder style={{ display: news.image ? 'none' : 'flex' }}>
                    뉴스 이미지 {index + 1}
                  </NewsImagePlaceholder>
                </NewsImageContainer>
                <NewsContent>
                  <NewsDate>{news.date}</NewsDate>
                  <NewsTitle>{news.title}</NewsTitle>
                </NewsContent>
              </NewsCard>
            ))}
          </NewsGrid>
        </AnimatedSection>

        <AnimatedSection ref={buttonRef} isVisible={buttonVisible} animation="fadeInUp" delay="0.4s">
          <MoreButtonContainer>
            <MoreButton onClick={handleMoreClick}>View All News →</MoreButton>
          </MoreButtonContainer>
        </AnimatedSection>
      </Container>
    </NewsSectionWrapper>
  );
}

export default NewsSection;
